// Thomas Jefferson University: public college page (#2839), official facts
// only.
//
// Which university: Thomas Jefferson University in Philadelphia (IPEDS unit
// 216366), the university Philadelphia University merged into in 2017. Its
// first-year undergraduate programs are taught mainly on the East Falls
// campus (the former Philadelphia University); the Center City campus is the
// university's address of record. Its Common Data Set is one report for the
// whole university, both Philadelphia campuses.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Jefferson's Common Data Set 2024-2025 (Office of Institutional Research
//    & Planning, East Falls; an .xlsx file read cell by cell). Departure:
//    2024-2025 is the newest edition its Common Data Set page posts, so the
//    counts are for Fall 2024, not Fall 2025. C1 applicants, admits and
//    enrollees for Fall 2024 (the residency table gives totals only, no
//    split, so no residency rows); C8 test policy, written for fall 2026
//    applicants; C21/C22 (no early decision; nonbinding, not restrictive
//    early action, with no counts). IPEDS (College Navigator, unit 216366)
//    shows the same men and women for Fall 2024 and 7,087 applicants in all,
//    five more than the CDS; the page uses the CDS.
//  - jefferson.edu First-Year Admissions, Apply ("Our 2027 application is
//    open"; Early Action November 1, Regular Decision March 1, the 4+2
//    Physician Assistant deadline of December 1, the test-optional policy,
//    supporting materials, the $50 fee).
//  - Jefferson Financial Aid Office, Cost of Attendance: East Falls
//    Undergraduate Programs, 2025-2026, first year, full-time day program,
//    dormitory column. Departure: the full 2026-2027 cost of attendance is
//    not posted yet (the tuition page lists 2026-2027 tuition and fees only),
//    so the page uses 2025-2026 and says so.
//  - College Scorecard API, school id 216366 (net price by family income).
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Jefferson Common Data Set 2024-2025',
        url: 'https://www.jefferson.edu/east-falls/institutional-research-and-planning/common-data-set.html',
        read: '2026-10-01',
    },
    apply: {
        label: 'Jefferson First-Year Admissions, Apply',
        url: 'https://www.jefferson.edu/admissions/undergraduate/first-year/apply.html',
        read: '2026-10-01',
    },
    coa: {
        label: 'Jefferson Financial Aid Office, Cost of Attendance, East Falls Undergraduate Programs 2025-2026',
        url: 'https://www.jefferson.edu/content/dam/academic/tuition-financial-aid/resources/EF-2526-COA.pdf',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?216366-Thomas-Jefferson-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'thomas-jefferson-university',
    kbId: 'thomas_jefferson_university',
    name: 'Thomas Jefferson University',
    shortName: 'Jefferson',
    aliases: ['TJU', 'Jefferson', 'Philadelphia University'],
    location: 'Philadelphia, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Jefferson athletics branding); decoration only.
    branding: {
        primary: '#1A2650',
        secondary: '#68BCE2',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/thomas_jefferson_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 7082,
        admitted: 5735,
        enrolled: 697,
        source: 'cds',
        where: 'section C1 (the 2024-2025 edition is the newest Jefferson posts)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Jefferson\'s Common Data Set reports one first-year acceptance rate for the whole university, both Philadelphia campuses, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Jefferson has no early decision plan. Its Early Action plan is nonbinding and not restrictive. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Jefferson through the Common App or the MyJefferson application. Some programs ask for more:',
        rows: [
            { school: 'Dual Degree 4+2 BS in Health Sciences / MS in Physician Assistant Studies', requirement: 'Its own application deadline of December 1.' },
            { school: 'Programs with a supplemental essay', requirement: 'Some programs ask you to address a specific topic in a supplemental essay; the application gives the prompt.' },
            { school: 'Design programs', requirement: 'A portfolio of your creative work is encouraged, but not required.' },
            { school: 'Some accelerated or dual degree programs', requirement: 'A summary of health-related activities, community service, shadowing or internship hours may be encouraged, but is not required.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'March 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'March 1', decision: null, binding: false },
        ],
        note: 'Both are priority deadlines: every required document must arrive by the date for your application to be complete and get priority consideration. The application fee is $50. Jefferson\'s admissions page does not publish decision dates.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Jefferson has dropped the test requirement for the programs that had one. You may still send an SAT or ACT score, but applying without one will not disadvantage you; Jefferson reviews each application as a whole.',
        source: 'apply',
        cdsLine: 'The 2024-2025 Common Data Set, written for fall 2026 applicants, also marks the SAT or ACT as not required for admission, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2025-2026',
        rows: [
            { item: 'Tuition', amount: 48022 },
            { item: 'Fees', amount: 1228 },
            { item: 'Books and supplies', amount: 2500 },
            { item: 'Housing and meals (on campus)', amount: 16387 },
            { item: 'Transportation and personal expenses', amount: 2725 },
        ],
        total: 70862,
        note: 'For a first-year, full-time day student in an East Falls undergraduate program living on campus, fall and spring. Jefferson has posted 2026-2027 tuition but not yet its full 2026-2027 cost of attendance, so these are the 2025-2026 figures.',
        source: 'coa',
        netPrice: {
            average: 25555,
            byIncome: [
                { income: '$0 to $30,000', amount: 20022 },
                { income: '$30,001 to $48,000', amount: 21921 },
                { income: '$48,001 to $75,000', amount: 22768 },
                { income: '$75,001 to $110,000', amount: 26510 },
                { income: 'Over $110,000', amount: 32087 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Jefferson\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
